import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const GymEditModal = ({ gym, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    isActive: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (gym) {
      setFormData({
        name: gym?.name || '',
        address: gym?.address || '',
        city: gym?.city || '',
        state: gym?.state || '',
        zipCode: gym?.zip_code || '',
        phone: gym?.phone || '',
        email: gym?.email || '',
        isActive: gym?.is_active ?? true
      });
    }
  }, [gym]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Building2" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground">
                Edit Gym Location
              </h2>
              <p className="text-sm text-muted-foreground">Update gym information</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Gym Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Gym Name <span className="text-error">*</span>
            </label>
            <Input
              type="text"
              name="name"
              value={formData?.name}
              onChange={handleChange}
              placeholder="Enter gym name"
              required
            />
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Street Address <span className="text-error">*</span>
            </label>
            <Input
              type="text"
              name="address"
              value={formData?.address}
              onChange={handleChange}
              placeholder="Enter street address"
              required
            />
          </div>

          {/* City, State, Zip */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                City <span className="text-error">*</span>
              </label>
              <Input
                type="text"
                name="city"
                value={formData?.city}
                onChange={handleChange}
                placeholder="City"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                State <span className="text-error">*</span>
              </label>
              <Input
                type="text"
                name="state"
                value={formData?.state}
                onChange={handleChange}
                placeholder="State"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Zip Code <span className="text-error">*</span>
              </label>
              <Input
                type="text"
                name="zipCode"
                value={formData?.zipCode}
                onChange={handleChange}
                placeholder="Zip Code"
                required
              />
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Phone Number
              </label>
              <Input
                type="tel"
                name="phone"
                value={formData?.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Email Address
              </label>
              <Input
                type="email"
                name="email"
                value={formData?.email}
                onChange={handleChange}
                placeholder="gym@example.com"
              />
            </div>
          </div>

          {/* Active Status */}
          <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
            <input
              type="checkbox"
              id="isActive"
              name="isActive"
              checked={formData?.isActive}
              onChange={handleChange}
              className="w-4 h-4 rounded border-input text-primary focus:ring-2 focus:ring-ring"
            />
            <label htmlFor="isActive" className="text-sm font-medium text-foreground cursor-pointer">
              Active Status
            </label>
            <span className="text-xs text-muted-foreground ml-auto">
              {formData?.isActive ? 'Gym is active' : 'Gym is inactive'}
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              loading={saving}
            >
              <Icon name="Save" size={16} className="mr-2" />
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GymEditModal;
